import React, { useEffect, useState } from "react"
import ModalComponent from "../../components/Modal"
import { Col, FloatingLabel, Form, Row, Spinner, Button } from "react-bootstrap"
import CustomSelect from "../../components/CustomSelect"
import { Map } from "@vis.gl/react-google-maps"
import { useFormik } from "formik"
import { toast } from "react-toastify"
import * as Yup from "yup"
import { admin } from "../../config/apiEndpoints"
import axiosProvider from "../../lib/AxiosProvider"
import { handleOnBlur, handleOnchange } from "../../lib/GlobalMethods"
import { _latitudeRegex_, _longitudeRegex_ } from "../../lib/Regex"

const getStationCoordinates = (station) => {
  const location = Array.isArray(station?.location)
    ? station.location[0]
    : station?.location
  const lat = Number(location?.lat ?? location?.latitude)
  const lng = Number(location?.lng ?? location?.longitude)

  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null
}

const AddEditChargingStation = ({
  handleClose,
  show,
  setShow,
  fetchData,
  setIsUpdate,
  setData,
  isUpdate,
}) => {
  const [marker, setMarker] = useState({
    lat: 23.0042882,
    lng: 72.500343,
  })
  const [loading, setLoading] = useState(false)
  const [chargerData, setChargerData] = useState([])
  const [amenitiesData, setAmenitiesData] = useState([])

  const validationSchema = Yup.object({
    station_name: Yup.string().required("Charging station name is required"),
    address: Yup.string().required("Charging station address is required"),
    latitude: Yup.string().required("Latitude is required"),
    longitude: Yup.string().required("Longitude is required"),
    amenities: Yup.array()
      .min(1, "At least one amenity is required")
      .required("Amenities are required"),
    chargers: Yup.array()
      .min(1, "At least one charger is required")
      .required("Charger is required"),
  })

  const handleSubmit = async (values, { resetForm }) => {
    try {
      setLoading(true)
      values = {
        station_name: values?.station_name,
        address: values?.address,
        parking_fee: Number(values?.parking_fee),
        latitude: Number(values?.latitude),
        longitude: Number(values?.longitude),
        amenities: Array.isArray(values?.amenities)
          ? values?.amenities.map((amenity) => Number(amenity?.value))
          : values?.amenities?.value,
        chargers: Array.isArray(values?.chargers)
          ? values?.chargers.map((charger) => charger?.value)
          : values?.chargers?.value,
      }

      const response = await axiosProvider({
        method: isUpdate ? "PATCH" : "POST",
        endpoint: isUpdate
          ? admin?.updateChargingStation + isUpdate?.id
          : admin?.createChargingStation,
        data: values,
      })
      if (response?.data?.code === 201 || response?.data?.code === 200) {
        toast.success(response.data.message)
        setLoading(false)
        setShow(false)
        setIsUpdate(false)
        isUpdate
          ? setData((prevData) =>
              prevData.map((item) =>
                item.id === isUpdate.id
                  ? {
                      ...item,
                      ...response?.data?.station,
                    }
                  : item
              )
            )
          : fetchData()
        resetForm()
      } else {
        setLoading(false)
        throw { message: response.data.message }
      }
    } catch (errors) {
      setLoading(false)
      toast.error(errors.message)
    }
  }

  const fetchChargerData = async () => {
    try {
      const response = await axiosProvider({
        method: "GET",
        endpoint: admin.unassignedChargerList,
      })
      const data = response?.data?.data?.map(({ id }) => ({
        value: id,
        label: id,
      }))
      setChargerData(data)
    } catch (error) {
      toast.error(error.message)
    }
  }

  const fetchAmenitiesData = async () => {
    try {
      const response = await axiosProvider({
        method: "GET",
        endpoint: admin.amenityList,
      })
      const data = response?.data?.data?.map(
        ({ id, amenity_img, amenity_name }) => ({
          value: id,
          label: amenity_name,
          image: amenity_img,
        })
      )
      setAmenitiesData(data)
    } catch (error) {
      toast.error(error.message)
    }
  }

  const formik = useFormik({
    initialValues: isUpdate
      ? {
          station_name: isUpdate.station_name,
          address: isUpdate.address,
          latitude: getStationCoordinates(isUpdate)?.lat ?? "",
          longitude: getStationCoordinates(isUpdate)?.lng ?? "",
          amenities: isUpdate.amenities.map((data) => ({
            value: data.id,
            label: data.amenity_name,
            image: data.amenity_img,
          })),
          chargers: isUpdate.chargers.map((data) => ({
            value: data.id,
            label: data.id,
          })),
        }
      : {
          station_name: "",
          address: "",
          latitude: "",
          longitude: "",
          parking_fee: "",
          amenities: [],
          chargers: [],
        },
    validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  })

  const { errors, values, touched, resetForm, setFieldValue } = formik

  const handleMapDrag = (e) => {
    const newLat = e.detail.center.lat
    const newLng = e.detail.center.lng
    setMarker({ lat: newLat, lng: newLng })
    setFieldValue("latitude", newLat)
    setFieldValue("longitude", newLng)
  }

  const handleCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords
          setMarker({ lat: latitude, lng: longitude })
          setFieldValue("latitude", latitude)
          setFieldValue("longitude", longitude)
        },
        (error) => {
          toast.error(error)
        }
      )
    } else {
      toast.error("Geolocation is not supported by this browser.")
    }
  }

  const handleChargerChange = (selectedChargers) => {
    const deselectedChargerIds = values.chargers
      .filter(
        (charger) => !selectedChargers.some((s) => s.value === charger.value)
      )
      .map((charger) => charger.value)

    if (deselectedChargerIds.length > 0) {
      const deselectedChargers = deselectedChargerIds.map((id) => ({
        value: id,
        label: id,
      }))
      setChargerData((prevData) => {
        const uniqueDeselect = deselectedChargers.filter(
          (charger) =>
            !prevData.some(
              (existingCharger) => existingCharger.value === charger.value
            )
        )
        return [...prevData, ...uniqueDeselect]
      })
    }

    setFieldValue("chargers", selectedChargers)
  }

  useEffect(() => {
    fetchAmenitiesData()
    fetchChargerData()
  }, [])

  useEffect(() => {
    if (isUpdate) {
      const coordinates = getStationCoordinates(isUpdate)
      if (coordinates) setMarker(coordinates)
    }
    fetchChargerData()
  }, [isUpdate])

  return (
    <ModalComponent
      show={show}
      onHide={() => {
        handleClose()
        resetForm()
      }}
      modalsize={"lg"}
      backdrop="static"
      animationFade={true}
      modeltitle={isUpdate ? "Update Charging Station" : "Add Charging Station"}
      submitname={
        loading ? (
          <Spinner size="sm" animation="border" variant="white" />
        ) : isUpdate ? (
          "Update"
        ) : (
          "Add"
        )
      }
      btnclosetext={"Cancel"}
      handleSubmit={formik.handleSubmit}
      children={
        <>
          <Form noValidate>
            <Row xs={"auto"}>
              <Col xs={12}>
                <FloatingLabel
                  controlId="station_name"
                  label={
                    <>
                      <span>Station Name</span>
                      <span className="required_star">*</span>
                    </>
                  }
                  className="mb-3"
                >
                  <Form.Control
                    type="text"
                    placeholder=""
                    name="station_name"
                    autoComplete="false"
                    maxLength={50}
                    value={values.station_name}
                    onChange={(e) => handleOnchange(e, false, formik)}
                    onBlur={(e) => handleOnBlur(e, formik)}
                    isInvalid={touched.station_name && errors.station_name}
                    isValid={touched.station_name && !errors.station_name}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.station_name}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>
              <Col xs={12}>
                <FloatingLabel
                  controlId="station_address"
                  label={
                    <>
                      <span>Address</span>
                      <span className="required_star">*</span>
                    </>
                  }
                  className="mb-3"
                >
                  <Form.Control
                    as="textarea"
                    placeholder=""
                    name="address"
                    autoComplete="false"
                    maxLength={100}
                    value={values.address}
                    onChange={(e) => handleOnchange(e, false, formik)}
                    onBlur={(e) => handleOnBlur(e, formik)}
                    isInvalid={touched.address && errors.address}
                    isValid={touched.address && !errors.address}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.address}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>

              <Col xs={6} className="custom_selectinvalid_bdr mb-3">
                <label>
                  Charger<span className="required_star">*</span>
                </label>
                <CustomSelect
                  value={values?.chargers}
                  options={chargerData}
                  handleSelectChange={handleChargerChange}
                  isMulti={true}
                  placeholder={"Select Charger"}
                />
                {touched?.chargers && errors.chargers && (
                  <p className="invalid_error">{errors.chargers}</p>
                )}
              </Col>

              <Col xs={6} className="custom_selectinvalid_bdr mb-3">
                <label>
                  Amenities<span className="required_star">*</span>
                </label>
                <CustomSelect
                  options={amenitiesData}
                  value={values?.amenities}
                  handleSelectChange={(selectedOption) => {
                    setFieldValue("amenities", selectedOption)
                  }}
                  getOptionLabel={(item) => (
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <img
                        src={process.env.REACT_APP_RESOURCE_URL + item.image}
                        alt={item.label}
                        style={{
                          width: 26,
                          height: 26,
                          marginRight: 10,
                          objectFit: "contain",
                        }}
                      />
                      {item.label}
                    </div>
                  )}
                  isMulti={true}
                  placeholder={"Select Amenities"}
                />
                {touched?.amenities && errors.amenities && (
                  <p className="invalid_error">{errors.amenities}</p>
                )}
              </Col>
              <Col xs={6}>
                <FloatingLabel
                  controlId="latitude"
                  label={
                    <>
                      <span>Latitude</span>
                      <span className="required_star">*</span>
                    </>
                  }
                  className="mb-3"
                >
                  <Form.Control
                    type="text"
                    placeholder=""
                    name="latitude"
                    autoComplete="false"
                    maxLength={20}
                    value={values.latitude}
                    onChange={(e) => handleOnchange(e, _latitudeRegex_, formik)}
                    onBlur={(e) => {
                      setMarker((prev) => ({
                        ...prev,
                        lat: parseFloat(e?.target?.value),
                      }))
                    }}
                    isInvalid={touched.latitude && errors.latitude}
                    isValid={touched.latitude && !errors.latitude}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.latitude}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>
              <Col xs={6}>
                <FloatingLabel
                  controlId="longitude"
                  label={
                    <>
                      <span>Longitude</span>
                      <span className="required_star">*</span>
                    </>
                  }
                  className="mb-3"
                >
                  <Form.Control
                    type="text"
                    placeholder=""
                    name="longitude"
                    autoComplete="false"
                    maxLength={20}
                    value={values.longitude}
                    onChange={(e) =>
                      handleOnchange(e, _longitudeRegex_, formik)
                    }
                    onBlur={(e) => {
                      setMarker((prev) => ({
                        ...prev,
                        lng: parseFloat(e?.target?.value),
                      }))
                    }}
                    isInvalid={touched.longitude && errors.longitude}
                    isValid={touched.longitude && !errors.longitude}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.longitude}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>
              <Col xs={12}>
                <div className="position-relative">
                  <Map
                    style={{ width: "100%", height: "300px" }}
                    center={marker}
                    defaultZoom={16}
                    gestureHandling={false}
                    disableDefaultUI={false}
                    onBoundsChanged={handleMapDrag}
                    fullscreenControl={false}
                    options={{ scrollwheel: false }}
                  ></Map>
                  <i className="icon_main location_pin locationsetpin"></i>
                  <span
                    className="currentlocation_btn"
                    onClick={handleCurrentLocation}
                  >
                    <i className="icon_main currect_location"></i>
                  </span>
                </div>
              </Col>
            </Row>
          </Form>
        </>
      }
    />
  )
}

export default AddEditChargingStation
